import React from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { injectIntl } from 'react-intl';
import { FormattedMessage } from '../../i18n/FormattedMessage';
import PageLoader from '../../components/PageLoader';
import AriesColGroup from './AriesColGroup';
import AriesHeaderLabel from './AriesHeaderLabel';

/**
 * AriesTable — reusable, presentational list table matching the Aries redesign
 * (gradient header bar + white rounded card rows). Flat, semantic markup (no
 * nested tables) so styling is predictable and promotable platform-wide.
 *
 * It is a drop-in for the subset of ReusableTable props used by list views:
 * it renders a styled header from `headerData` and delegates each body row to
 * the injected `RowToRender` / `StaticRowToRender` components (which emit the
 * `<td>` cells), so existing row logic is preserved unchanged.
 */
const { useRef, useState, useLayoutEffect } = React;

const AriesTable = ({
  headerData,
  bodyData,
  RowToRender,
  RowToRenderProps,
  loadMore,
  hasMoreItems,
  isLoaderVisible,
  isEndMessageVisible,
  endMessage,
  sortData,
  StaticRowToRender,
  staticRowProps,
  shouldRenderStaticRow,
  showFullTableLoader,
  height,
  intl,
}) => {
  // `height="fill"` makes the scroll region always reach near the viewport
  // bottom (leaving a small gap): measure the scroll region's live top offset
  // (which accounts for ALL markup above it: top bar margin, page header,
  // search/filter row, padding) and derive the remaining space. Recomputed on
  // resize, on data changes, after paint (rAF), and whenever layout above
  // shifts (ResizeObserver on document.body). Reusable alternative to a magic
  // per-view pixel offset; stacked tables still use height={null}.
  const scrollTopRef = useRef(null);
  const [fillHeight, setFillHeight] = useState(0);
  const isFillMode = height === 'fill';

  useLayoutEffect(() => {
    if (!isFillMode) return undefined;
    const bottomGap = 48;
    const compute = () => {
      const el = scrollTopRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const next = Math.max(0, globalThis.window.innerHeight - top - bottomGap);
      setFillHeight((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    };
    compute();
    const raf = globalThis.window.requestAnimationFrame(compute);
    globalThis.window.addEventListener('resize', compute);
    let observer;
    if (globalThis.ResizeObserver) {
      observer = new globalThis.ResizeObserver(compute);
      observer.observe(globalThis.document.body);
    }
    return () => {
      globalThis.window.cancelAnimationFrame(raf);
      globalThis.window.removeEventListener('resize', compute);
      observer?.disconnect();
    };
  }, [isFillMode, bodyData.length]);

  const resolvedHeight = isFillMode ? fillHeight : height;
  // Only constrain the scroll container when a positive height is given.
  // When height is null/0, the table grows with content and uses window scroll
  // (needed when several tables are stacked on one page).
  const useFixedHeight =
    typeof resolvedHeight === 'number' && resolvedHeight > 0;
  const scrollProps = useFixedHeight ? { height: resolvedHeight } : {};
  return (
    <div className="aries-table-wrap">
      <table className="aries-table">
        <AriesColGroup headerData={headerData} />
        <thead>
          <tr className="aries-table-head">
            {headerData.map((header, index) => (
              <th
                key={header.title + index}
                style={header.width ? { width: header.width } : undefined}
              >
                {header.isSortable ? (
                  <button
                    type="button"
                    className="aries-table-sort"
                    onClick={() => sortData(index)}
                    data-tip={
                      header.dataTip &&
                      intl.formatMessage({ id: header.dataTip })
                    }
                    data-for={header.dataFor}
                  >
                    <span
                      className="aries-table-sort-icon"
                      aria-hidden="true"
                    />
                    <FormattedMessage
                      id={header.title}
                      defaultMessage={header.title}
                    />
                  </button>
                ) : (
                  <AriesHeaderLabel header={header} index={index} />
                )}
              </th>
            ))}
          </tr>
        </thead>
      </table>

      {shouldRenderStaticRow && StaticRowToRender && (
        <table className="aries-table aries-table-body">
          <AriesColGroup headerData={headerData} />
          <tbody>
            <StaticRowToRender {...staticRowProps} />
          </tbody>
        </table>
      )}

      <div ref={scrollTopRef} className="aries-table-scroll-top" />
      <InfiniteScroll
        dataLength={bodyData.length}
        next={loadMore}
        hasMore={hasMoreItems}
        {...scrollProps}
        endMessage={
          isEndMessageVisible ? (
            <div className="aries-table-empty">
              <FormattedMessage id={endMessage} defaultMessage={endMessage} />
            </div>
          ) : (
            ''
          )
        }
      >
        {showFullTableLoader ? (
          <PageLoader />
        ) : (
          <table className="aries-table aries-table-body">
            <AriesColGroup headerData={headerData} />
            <tbody>
              {bodyData?.map(
                (row, index) =>
                  row && (
                    <RowToRender
                      key={row._id || row + index}
                      row={row}
                      index={index}
                      {...RowToRenderProps}
                    />
                  )
              )}
            </tbody>
          </table>
        )}
        {!showFullTableLoader && isLoaderVisible ? <PageLoader /> : null}
      </InfiniteScroll>
    </div>
  );
};

AriesTable.propTypes = {
  headerData: PropTypes.array,
  bodyData: PropTypes.array,
  RowToRender: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  RowToRenderProps: PropTypes.object,
  loadMore: PropTypes.func,
  hasMoreItems: PropTypes.bool,
  isLoaderVisible: PropTypes.bool,
  isEndMessageVisible: PropTypes.bool,
  endMessage: PropTypes.string,
  sortData: PropTypes.func,
  StaticRowToRender: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  staticRowProps: PropTypes.object,
  shouldRenderStaticRow: PropTypes.bool,
  showFullTableLoader: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  intl: PropTypes.object,
};

AriesTable.defaultProps = {
  headerData: [],
  bodyData: [],
  RowToRender: undefined,
  RowToRenderProps: {},
  loadMore: () => {},
  hasMoreItems: false,
  isLoaderVisible: true,
  isEndMessageVisible: false,
  endMessage: 'NO_MORE_DATA',
  sortData: () => {},
  StaticRowToRender: undefined,
  staticRowProps: {},
  shouldRenderStaticRow: false,
  showFullTableLoader: false,
  height: window.innerHeight - 390,
};

export default injectIntl(AriesTable);
